import { call, put, takeLatest, takeEvery, select, delay, all } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { PaginationType } from 'constants/Global';
import { SELF_SERVICE_TYPE } from 'constants/SelfServiceConstants';
import { putAndWait } from 'modules/app/sagas';
import { simpleRequestsLoader } from 'modules/app/utils';
import * as chatActions from 'modules/chat/actions/details';
import { REFRESH_INTERVENTION_CHAT_ACTIONS } from 'modules/chat/reducer/ui/details/isLoading';
import { getMessageEntityMapping } from 'modules/chat/utils';
import * as interventionCoreActions from 'modules/interventions/actions/core';
import * as interventionDetailsActions from 'modules/interventions/actions/details';
import * as interventionKanbanActions from 'modules/interventions/actions/kanban';
import { getById } from 'modules/interventions/selectors';
import { shouldResendSelfService } from 'modules/interventions/utils';
import { deleteMobility } from 'modules/selfServices/actions';
import { createNotification } from 'modules/toast-notifications/actions';
import { downloadURL, downloadFile } from 'services/Utils';

import * as actionTypes from '../actionTypes';

function* fetchInterventionPdfSaga(action) {
  const { request, name: fileName } = action.payload;
  try {
    const { data } = yield call(restClient.request, request);

    typeof data === 'object' && data.path
      ? downloadURL(data.path)
      : downloadFile(data, { fileName, type: 'application/pdf' });

    yield put(interventionDetailsActions.fetchInterventionPdfRequestSuccess(data));
  } catch (message) {
    yield put(
      createNotification({
        type: 'error',
        message: 'http.error.message',
      }),
    );
    yield put(interventionDetailsActions.fetchInterventionPdfRequestError(message));
  }
}

function* postCreateReminderWorker(action) {
  const { request } = action.payload;
  let messageDescriptor = { id: 'page.fiche.notification.create_reminder.success', defaultMessage: 'Action processed' };

  try {
    const response = yield call(restClient.request, request);
    const { data } = response;
    yield put(interventionDetailsActions.createInterventionReminderSuccess(data));

    if (data?.length > 0) {
      if (data.length > 1) {
        messageDescriptor = {
          values: { count: data.length },
          defaultMessage: '{count} Reminders created',
          id: 'page.reminders.notification.create.manySuccess',
        };
      } else {
        messageDescriptor = { id: 'page.reminders.notification.create.success', defaultMessage: 'Reminder created' };
      }
    }
    yield put(createNotification({ type: 'success', message: messageDescriptor }));
  } catch (error) {
    yield put(interventionDetailsActions.createInterventionReminderFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: { id: 'page.fiche.notification.create_reminder.failure', defaultMessage: "Can't create the reminder" },
      }),
    );
  }
}

// Update chat after a send
function* afterSendInterventionWorker({ payload }) {
  // Chat messages are created asynchronously AFTER the intervention is actually sent.
  // So we have to "wait" for the messages to be created. This could be avoided using WebSockets 😉
  yield delay(2000);

  const intervention = payload.intervention ?? payload;

  yield put(
    chatActions.fetchMessages({
      conversationId: intervention.conversationId,
      paginationType: PaginationType.SLICE,
      ...getMessageEntityMapping({ interventionId: intervention.id }),
    }),
  );
}

/**
 * @param {import('../types/projections/detailed-intervention').DetailedIntervention} intervention
 */
function* beforeSaveIntervention(intervention) {
  const reduxIntervention = yield select(state => getById(state, intervention.id));

  const removeIds = Object.values(SELF_SERVICE_TYPE).reduceRight((acc, type) => {
    const initialSelfService = reduxIntervention?.selfServices?.find(selfService => selfService.type === type);
    const selfService = intervention.selfServices?.find(self => self.type === type);

    const shouldDeleteMobility = selfService && initialSelfService?.mobility?.id && !selfService?.mobility?.id;
    if (shouldDeleteMobility) {
      acc.push(initialSelfService.mobility.id);
    }

    return acc;
  }, []);

  let payloadError = null;
  for (const mobilityId of removeIds) {
    const { payload, error } = yield putAndWait(deleteMobility(mobilityId));
    payloadError = error ? payload : payloadError;
  }

  if (payloadError) {
    throw new Error(payloadError);
  }
}

/** @param {import('modules/interventions/types/actions').UpdateInterventionRequestAction} action */
function* updateInterventionWorker({ payload, meta }) {
  const { request } = payload;

  try {
    const { conversationId } = payload.request.data;
    /** @type {AxiosResponse<import('modules/interventions/types/projections/detailed-intervention').DetailedIntervention>} */
    yield call(beforeSaveIntervention, request.data);
    const { data: intervention } = yield call(restClient.request, request);

    const reduxIntervention = yield select(state => getById(state, intervention.id));
    const checkIn = reduxIntervention?.selfServices?.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_IN);

    const shouldResend = shouldResendSelfService(
      checkIn,
      intervention?.selfServices?.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_IN)?.mobility,
    );

    yield put(
      interventionCoreActions.updateInterventionSuccess(
        intervention,
        shouldResend ? { action: 'resend', ...meta } : meta,
      ),
    );

    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.update_intervention.success',
          defaultMessage: 'File updated',
        },
      }),
    );

    const haveNewConversation = intervention.conversationId !== conversationId;
    if (haveNewConversation) {
      yield call(afterSendInterventionWorker, { payload: intervention });
    }
  } catch (error) {
    yield put(interventionCoreActions.updateInterventionFailure(error, meta));
  }
}

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [actionTypes.FETCH_INTERVENTION_KANBAN_DETAILS_REQUEST]: {
    callbacks: {
      failure: (error, meta, { payload }) =>
        interventionKanbanActions.fetchInterventionKanbanDetailsFailure(payload.interventionId, error, meta),
    },
  },
  [actionTypes.PUSH_BACK_DMS_REQUEST]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.pushBackDMS.success',
        defaultMessage: 'DMS information pushed back successfully',
      },
      failure: {
        id: 'page.fiche.notification.pushBackDMS.failure',
        defaultMessage: 'Failed to push back DMS information',
      },
    },
  },
  [actionTypes.SEND_INTERVENTION_REQUEST]: {
    callbacks: {
      success: interventionDetailsActions.sendInterventionSuccess,
      failure: interventionDetailsActions.sendInterventionFailure,
    },
    notifications: {
      success: {
        id: 'page.fiche.notification.send_intervention.success',
        defaultMessage: 'File sent',
      },
      failure: {
        id: 'http.error.message',
        defaultMessage: 'An error occurred',
      },
    },
  },
  [actionTypes.POST_SEND_BY_REQUEST]: {
    notifications: {
      success: {
        id: 'page.fiche.notification.send_appointment.success',
        defaultMessage: 'Appointment sent',
      },
      failure: {
        id: 'page.fiche.notification.send_appointment.failure',
        defaultMessage: 'Can’t send the appointment',
      },
    },
  },
  [actionTypes.CREATE_DEFAULT_STOCK_CHECKLIST_REQUEST]: {
    callbacks: {
      success: interventionDetailsActions.createStockFileChecklistsSuccess,
      failure: interventionDetailsActions.createStockFileChecklistsFailure,
    },
    notifications: {
      success: {
        id: 'page.fiche.addOfferModal.import.successStock',
        defaultMessage: 'Offer successfully created.',
      },
      failure: {
        id: 'page.fiche.addOfferModal.import.error',
        defaultMessage: 'An error occurred during the import process.',
      },
    },
  },
  [actionTypes.CREATE_INTERVENTION_REQUEST]: {},
  [actionTypes.FETCH_INTERVENTIONS_REQUEST]: {},
  [actionTypes.FETCH_INTERVENTION_LINK_REQUEST]: {},
  [actionTypes.READ_INTERVENTION_MESSAGES_REQUEST]: {
    callbacks: {
      success: (_, __, { payload }) => interventionDetailsActions.readInterventionMessagesSuccess(payload.id),
    },
  },
  [actionTypes.FETCH_INTERVENTION_REQUEST]: {
    effect: takeEvery,
    callbacks: {
      success: (data, meta) =>
        interventionCoreActions.fetchInterventionSuccess(
          {
            ...data,
            dealerId: data.dealerId ?? data.dealer?.id,
          },
          meta,
        ),
      failure: (error, meta) => interventionCoreActions.fetchInterventionFailure(error, meta),
    },
  },
  [actionTypes.UPDATE_INTERVENTION_STATUS_REQUEST]: {
    effect: takeEvery,
  },
  [actionTypes.DELETE_INTERVENTION_REQUEST]: {
    effect: takeEvery,
    callbacks: {
      success: (_, meta, { payload }) => interventionCoreActions.deleteInterventionSuccess(payload.interventionId, meta),
    },
  },
  [actionTypes.DELAY_REMINDER_REQUEST]: {
    effect: takeEvery,
    notifications: {
      success: {
        id: 'page.fiche.notification.update_intervention.success',
        defaultMessage: 'File updated',
      },
      failure: {
        id: 'page.fiche.notification.update_intervention.failure',
        defaultMessage: "Can't update the file",
      },
    },
  },
  [actionTypes.POST_DUPLICATE_INTERVENTION]: {},
  [actionTypes.SEND_UNIQUE_VIDEO_REQUEST]: {
    callbacks: {
      success: (_, meta, { payload }) => interventionDetailsActions.sendUniqueVideoSuccess(payload.interventionId, meta),
      failure: (error, meta) => interventionDetailsActions.sendUniqueVideoFailure(error, meta),
    },
    notifications: {
      success: {
        id: 'page.fiche.uniqueVideo.export_success',
        defaultMessage: 'Export in progress. Please check your inbox in a few minutes.',
      },
    },
  },
};

export default function* interventionSagas() {
  yield all(simpleRequestsLoader(simpleRequests));

  // Backbones
  yield takeEvery(actionTypes.UPDATE_INTERVENTION_REQUEST, updateInterventionWorker);

  yield takeLatest(actionTypes.FETCH_INTERVENTIONS_PDF, fetchInterventionPdfSaga);
  yield takeLatest(REFRESH_INTERVENTION_CHAT_ACTIONS, afterSendInterventionWorker);
  yield takeLatest(actionTypes.CREATE_INTERVENTION_REMINDER_REQUEST, postCreateReminderWorker);
}
